import { useCallback, useEffect, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { CaretLeft, ChatCircleDots } from "@phosphor-icons/react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Loader } from "@/components/layout/loader";
import {
  AdsIntelligenceApiFactory,
  inferRangePreset,
  type DashboardRange,
} from "@/apis/paid-media";
import { api } from "@/config/api";
import { cn, parseUTCDate } from "@/lib/utils";

import { RangeSelector } from "@/components/dashboard/range-selector";
import { RightPanel } from "@/components/right-panel/right-panel";
import { DashboardSectionsProvider } from "@/context/dashboard-sections";
import { useAuthenticationStore } from "@/store/authentication";

export const Route = createFileRoute("/dashboards/share/$shareToken")({
  component: SharedDashboardPage,
});

const REFRESH_BUFFER_MS = 60_000;
const CACHE_PREFIX = "shared-dashboard:";

/** Allowed presets for the date pill selector. */
const RANGE_PRESETS: readonly DashboardRange[] = ["7d", "30d", "90d"] as const;
/**
 * How long to wait before showing the iframe-blocking overlay at all. Cached
 * `hit` switches resolve in ~100ms — flashing a full backdrop for sub-second
 * cases feels heavier than the operation actually was. The pill spinner shows
 * immediately and is enough feedback. The overlay kicks in for the slower
 * `revived` (~30s) and `generated` (~90–120s) paths.
 */
const OVERLAY_DELAY_MS = 600;
/**
 * Hard ceiling on a single refresh request. `generated` (cache-miss agent
 * regenerate) can take up to ~120s in the typical case; 240s gives plenty of
 * headroom for the 95th-percentile path before we give up and surface a
 * timeout. Backend handoff explicitly asks for ≥ 180s.
 */
const REFRESH_CLIENT_TIMEOUT_MS = 240_000;

function isValidRange(v: unknown): v is DashboardRange {
  return typeof v === "string" && (RANGE_PRESETS as readonly string[]).includes(v);
}

const RANGE_LABEL: Record<DashboardRange, string> = {
  "7d": "last 7 days",
  "30d": "last 30 days",
  "90d": "last 90 days",
};

// localStorage keys for panel persistence. Scoped per-token would be overkill;
// users expect a single panel preference across dashboards.
const PANEL_OPEN_KEY = "share-panel:open";
const PANEL_WIDTH_KEY = "share-panel:width";
const DEFAULT_PANEL_WIDTH = 400;
const MIN_PANEL_WIDTH = 320;
// Hard ceiling. The actual cap is min(this, ~80% of viewport) and is computed at drag time
// inside `RightPanel`, so bigger monitors get more room without ever swallowing the dashboard.
const MAX_PANEL_WIDTH = 1200;

function cacheGet(token: string) {
  try {
    const raw = localStorage.getItem(CACHE_PREFIX + token);
    return raw ? (JSON.parse(raw) as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function cacheSet(token: string, data: Record<string, unknown>) {
  try {
    localStorage.setItem(CACHE_PREFIX + token, JSON.stringify(data));
  } catch { /* quota exceeded */ }
}

/** Read persisted panel state up-front so the first render is correct (no flash). */
function readInitialPanelOpen(): boolean {
  try {
    const raw = localStorage.getItem(PANEL_OPEN_KEY);
    if (raw === null) return true; // default: open
    return raw === "1";
  } catch {
    return true;
  }
}

function readInitialPanelWidth(): number {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    if (!raw) return DEFAULT_PANEL_WIDTH;
    const n = parseInt(raw, 10);
    if (!Number.isFinite(n)) return DEFAULT_PANEL_WIDTH;
    return Math.min(MAX_PANEL_WIDTH, Math.max(MIN_PANEL_WIDTH, n));
  } catch {
    return DEFAULT_PANEL_WIDTH;
  }
}

function SharedDashboardPage() {
  const { shareToken } = Route.useParams();
  const navigate = Route.useNavigate();
  const user = useAuthenticationStore((state) => state.user);
  const organization = useAuthenticationStore((state) => state.organization);

  // Share routes are no longer fully public — they live behind the same auth gate as the rest of the app
  // because the backend middleware now requires a Bearer token to look up share-scoped resources.
  useEffect(() => {
    if (!user) {
      const next = window.location.pathname + window.location.search;
      navigate({ to: "/auth", search: { next } });
    }
  }, [user, navigate]);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notFound, setNotFound] = useState(false);
  const [title, setTitle] = useState("");
  const [dashboardType, setDashboardType] = useState<"inline" | "sandbox">("sandbox");
  const [inlineHtml, setInlineHtml] = useState<string | null>(null);
  const [currentUrl, setCurrentUrl] = useState("");
  const [timeoutAt, setTimeoutAt] = useState<string | null>(null);
  const [sandboxExpired, setSandboxExpired] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [panelOpen, setPanelOpen] = useState<boolean>(readInitialPanelOpen);
  const [panelWidth, setPanelWidth] = useState<number>(readInitialPanelWidth);
  // Active date preset. Stays `null` on first render so the pill highlight
  // doesn't leak from a previous session — `applyData` resolves it from the
  // server response (or from an inferred 7/30/90-day window). The selector
  // simply renders no active pill for the brief moment between mount and the
  // first response landing, which is honest about what we know.
  const [range, setRange] = useState<DashboardRange | null>(null);
  /**
   * Refresh-flow state machine:
   *   - "idle"     : no refresh in flight
   *   - "fetching" : request in flight, < OVERLAY_DELAY_MS old → pill spinner
   *                  only, no iframe overlay. Fast `hit` paths finish here.
   *   - "loading"  : request in flight, > OVERLAY_DELAY_MS → show full
   *                  overlay (`revived` ~30s and `generated` ~90–120s paths).
   *
   * The selector pills are disabled for the entire `fetching`/`loading`
   * window: each click on a never-seen-before preset costs ~90s of LLM time
   * server-side, so we don't let the user fire rapid-fire clicks.
   */
  const [refreshPhase, setRefreshPhase] = useState<"idle" | "fetching" | "loading">("idle");
  const [refreshingRange, setRefreshingRange] = useState<DashboardRange | null>(null);
  // In-flight refresh so we can abort it on unmount.
  const refreshControllerRef = useRef<AbortController | null>(null);
  // Timers we own (overlay-promotion + hard timeout).
  const escalationTimersRef = useRef<{ overlay?: number; hard?: number }>({});

  const handlePanelOpenChange = useCallback((next: boolean) => {
    setPanelOpen(next);
    try {
      localStorage.setItem(PANEL_OPEN_KEY, next ? "1" : "0");
    } catch { /* ignore */ }
  }, []);

  const handlePanelWidthCommit = useCallback((next: number) => {
    try {
      localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(next)));
    } catch { /* ignore */ }
  }, []);

  const applyData = useCallback((data: Record<string, unknown>) => {
    setTitle(data.title as string);
    if (data.type === "inline" && data.html) {
      setDashboardType("inline");
      setInlineHtml(data.html as string);
      setTimeoutAt(null);
      setSandboxExpired(false);
    } else {
      setDashboardType("sandbox");
      setCurrentUrl(data.url as string);
      const ta = data.timeout_at as string | null;
      setTimeoutAt(ta);
      const alive = ta ? parseUTCDate(ta).getTime() > Date.now() : !!(data.url);
      if (alive) setSandboxExpired(false);
      else setSandboxExpired(true);
    }
    // Backend is the source of truth for which range is currently rendered.
    // Prefer the explicit `range` field, but fall back to deriving it from
    // `date_range_start` / `date_range_end` when the bare `GET` (no `?range=`)
    // doesn't echo a preset string. Without this fallback the pill could be
    // wrong (or empty) even when the dashboard was clearly generated for a
    // recognisable 7/30/90-day window.
    const resolvedRange = isValidRange(data.range)
      ? (data.range as DashboardRange)
      : inferRangePreset(data.date_range_start as string | null, data.date_range_end as string | null);
    if (resolvedRange) {
      setRange(resolvedRange);
    }
    setLoading(false);
    setError(null);
    setNotFound(false);
  }, [shareToken]);

  const fetchDashboard = useCallback(
    async (opts?: { silent?: boolean }): Promise<boolean> => {
      if (!opts?.silent) {
        const cached = cacheGet(shareToken);
        if (cached && cached.type === "inline") {
          applyData(cached);
        } else {
          setLoading(true);
          setError(null);
          setNotFound(false);
        }
      }

      const endpoint = AdsIntelligenceApiFactory.ShareEndpoint + "/" + shareToken;
      console.log("[Share] fetching:", endpoint, opts?.silent ? "(silent)" : "");

      try {
        // Goes through `api()` so the user's Bearer token (and refresh-token retry)
        // is attached — share endpoints are no longer anonymous.
        const response = await api(endpoint, {
          method: "GET",
          headers: { Accept: "application/json" },
        });

        if (response.status === 404) {
          console.warn("[Share] 404 — dashboard not found");
          if (!cacheGet(shareToken)) {
            setLoading(false);
            setNotFound(true);
          }
          return false;
        }

        if (!response.ok) {
          const err = await response.json().catch(() => ({ message: "Failed to load dashboard" }));
          throw new Error(err.message || err.detail || "Failed to load dashboard");
        }

        const data = await response.json();
        console.log(
          "[Share] success — type:",
          data.type,
          "url:",
          data.url?.slice(0, 50),
          "timeout_at:",
          data.timeout_at,
        );
        cacheSet(shareToken, data);
        applyData(data);
        return true;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        console.warn("[Share] fetch failed:", msg);
        if (!opts?.silent) {
          if (!cacheGet(shareToken)) {
            setLoading(false);
            setError(msg);
          }
        }
        return false;
      }
    },
    [shareToken, applyData],
  );

  const handleRefresh = useCallback(async () => {
    setRefreshing(true);
    setSandboxExpired(false);
    const ok = await fetchDashboard({ silent: true });
    setRefreshing(false);
    if (!ok) setSandboxExpired(true);
  }, [fetchDashboard]);

  const clearEscalationTimers = useCallback(() => {
    const timers = escalationTimersRef.current;
    if (timers.overlay !== undefined) clearTimeout(timers.overlay);
    if (timers.hard !== undefined) clearTimeout(timers.hard);
    timers.overlay = undefined;
    timers.hard = undefined;
  }, []);

  /**
   * Pill click → regenerate the dashboard for a different preset range.
   *
   * Behavior is intentionally simple now that the backend is doing the heavy
   * lifting (per-range cache, deterministic latency tiers):
   *   - First click of a never-seen-before preset triggers a full agent
   *     regenerate (~90–120s). We disable all pills for the duration to
   *     prevent rapid-fire clicks from costing extra LLM time server-side.
   *   - Subsequent clicks of the same preset are sub-second cache hits.
   *   - Clicking the currently-rendered range is a no-op.
   *
   * Response codes: 200 ({hit, revived, generated}) | 400 | 404 | 502.
   */
  const handleRangeChange = useCallback(
    async (next: DashboardRange) => {
      // Pills are disabled during refresh, but defensively bail anyway.
      if (refreshControllerRef.current) return;
      if (next === range) return;

      const controller = new AbortController();
      refreshControllerRef.current = controller;

      // Hard timeout — backend says ≥ 180s for headroom; we use 240s.
      escalationTimersRef.current.hard = window.setTimeout(() => {
        controller.abort(new DOMException("Refresh timed out", "TimeoutError"));
      }, REFRESH_CLIENT_TIMEOUT_MS);

      // Overlay-promotion: don't show the heavy backdrop for fast `hit`
      // resolves. After OVERLAY_DELAY_MS we promote to "loading" so the
      // user gets clear visual feedback for `revived` / `generated`.
      escalationTimersRef.current.overlay = window.setTimeout(() => {
        if (refreshControllerRef.current !== controller) return;
        setRefreshPhase((prev) => (prev === "fetching" ? "loading" : prev));
      }, OVERLAY_DELAY_MS);

      setRefreshingRange(next);
      setRefreshPhase("fetching");

      try {
        const data = await AdsIntelligenceApiFactory.Apis.RefreshShare(
          shareToken,
          next,
          controller.signal,
        );

        // Cache so a hard reload doesn't flash a stale URL.
        cacheSet(shareToken, data as unknown as Record<string, unknown>);

        // Apply through the same pipeline the GET uses so iframe / timeout /
        // range stay coherent. The returned `url` is the new sandbox URL —
        // do NOT append `?range=` on the client (backend handles that now).
        applyData(data as unknown as Record<string, unknown>);
        setRange(next);

        // `hit` is too fast to bother. `revived` and `generated` were both
        // long enough to deserve a confirmation toast.
        if (data.cache_state === "revived" || data.cache_state === "generated") {
          toast.success("Dashboard refreshed", {
            description: `Now showing the ${RANGE_LABEL[next]}.`,
          });
        }
      } catch (err) {
        if (controller.signal.aborted) {
          if ((err as { name?: string })?.name === "TimeoutError") {
            toast.error("Refresh timed out", {
              description:
                "The dashboard took too long to load. Try again or pick another range.",
            });
          }
          return;
        }

        const status = (err as { status?: number })?.status;
        const message = err instanceof Error ? err.message : "Failed to refresh dashboard";
        if (status === 404) {
          toast.error("Dashboard not found", { description: "This dashboard no longer exists." });
        } else if (status === 502) {
          toast.error("Refresh failed", { description: message });
        } else {
          toast.error("Couldn't refresh dashboard", { description: message });
        }
      } finally {
        clearEscalationTimers();
        if (refreshControllerRef.current === controller) {
          refreshControllerRef.current = null;
          setRefreshPhase("idle");
          setRefreshingRange(null);
        }
      }
    },
    [range, shareToken, applyData, clearEscalationTimers],
  );

  // Make sure we don't leave a refresh dangling when the user navigates away.
  useEffect(() => {
    return () => {
      refreshControllerRef.current?.abort();
      const timers = escalationTimersRef.current;
      if (timers.overlay !== undefined) clearTimeout(timers.overlay);
      if (timers.hard !== undefined) clearTimeout(timers.hard);
    };
  }, []);

  useEffect(() => {
    fetchDashboard();
  }, [fetchDashboard]);

  // Sandbox keep-alive: polls every 15s, refreshes when close to expiry or already expired.
  useEffect(() => {
    if (!timeoutAt || dashboardType === "inline") return;
    let refreshInFlight = false;

    const check = async () => {
      if (refreshInFlight) return;
      const expiresAt = parseUTCDate(timeoutAt).getTime();
      const remaining = expiresAt - Date.now();
      console.log("[Share] poll — remaining:", Math.round(remaining / 1000) + "s");

      if (remaining <= REFRESH_BUFFER_MS) {
        console.log("[Share] within refresh window, calling API...");
        if (remaining <= 0) setRefreshing(true);
        refreshInFlight = true;
        const ok = await fetchDashboard({ silent: true });
        refreshInFlight = false;
        if (ok) {
          setRefreshing(false);
        } else if (remaining <= 0) {
          setSandboxExpired(true);
          setRefreshing(false);
        }
        return;
      }

      if (remaining <= 0) setSandboxExpired(true);
    };

    check();
    const interval = window.setInterval(check, 15_000);

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [timeoutAt, dashboardType, fetchDashboard]);

  // While the redirect-to-/auth effect is in-flight (or auth is still resolving in the root),
  // hold a loader instead of flashing the dashboard chrome.
  if (!user) {
    return <Loader />;
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-[100dvh] w-screen gap-4">
        <div className="size-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
        <p className="text-sm font-medium">Loading dashboard...</p>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="flex flex-col items-center justify-center h-[100dvh] w-screen gap-3">
        <p className="text-base font-semibold">Dashboard not found</p>
        <p className="text-sm text-muted-foreground">This link may be invalid or the dashboard may have been deleted.</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-[100dvh] w-screen gap-3">
        <p className="text-base font-semibold">Failed to load dashboard</p>
        <p className="text-sm text-muted-foreground">{error}</p>
        <Button size="sm" onClick={handleRefresh} disabled={refreshing} className="gap-2">
          {refreshing ? (
            <>
              <span className="size-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
              Refreshing...
            </>
          ) : (
            "Refresh Dashboard"
          )}
        </Button>
      </div>
    );
  }

  return (
    <DashboardSectionsProvider>
      <div
        className={cn("flex flex-col h-[100dvh] w-screen transition-[padding] duration-200")}
        style={panelOpen ? { paddingRight: panelWidth } : undefined}
      >
        <header className="flex items-center justify-between gap-3 px-4 h-12 border-b shrink-0 bg-background">
          <h1 className="text-sm font-semibold truncate min-w-0">{title}</h1>
          <div className="flex items-center gap-2 shrink-0">
            <RangeSelector
              value={range}
              busyValue={refreshPhase !== "idle" ? refreshingRange : null}
              disabled={refreshPhase !== "idle"}
              onChange={handleRangeChange}
            />
            {/* When the panel is hidden, surface a small "show panel" affordance in the header.
                When open, the panel owns its own collapse button — no need for a duplicate trigger. */}
            {!panelOpen ? (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 gap-1.5"
                onClick={() => handlePanelOpenChange(true)}
              >
                <CaretLeft weight="bold" className="size-3.5" />
                <ChatCircleDots weight="regular" className="size-4" />
                <span className="text-xs">Show panel</span>
              </Button>
            ) : null}
          </div>
        </header>
        <div className="relative flex-1 min-h-0">
          {dashboardType === "inline" && inlineHtml ? (
            <iframe
              srcDoc={inlineHtml}
              title={title}
              className="absolute inset-0 w-full h-full border-0"
              sandbox="allow-scripts"
            />
          ) : !currentUrl ? null : sandboxExpired ? (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-4">
              {refreshing ? (
                <>
                  <div className="size-8 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                  <p className="text-sm font-medium">Spinning up a fresh dashboard...</p>
                  <p className="text-xs text-muted-foreground">This may take 10-15 seconds</p>
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold">Dashboard sandbox has expired</p>
                  <p className="text-xs text-muted-foreground max-w-xs text-center">
                    The live sandbox timed out. Click below to spin up a fresh one.
                  </p>
                  <Button size="sm" onClick={handleRefresh} className="gap-2">
                    Refresh Dashboard
                  </Button>
                </>
              )}
            </div>
          ) : (
            <iframe
              src={currentUrl}
              title={title}
              className="absolute inset-0 w-full h-full border-0"
              allow="fullscreen"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
            />
          )}

          {/* Range-refresh overlay. Sits above the iframe so the previous
              dashboard isn't "frozen" looking — the user gets a clear signal
              that something is happening. Only shown after OVERLAY_DELAY_MS,
              so the fast `hit` (~100ms) path never triggers it; this
              overlay covers the `revived` (~30s) and `generated` (~90–120s)
              cases. The "first time slow, after that instant" line sets
              expectations for the worst-case `generated` path. */}
          {refreshPhase === "loading" ? (
            <div
              className={cn(
                "absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 px-6 text-center",
                "bg-background/85 backdrop-blur-sm transition-opacity duration-150",
              )}
              role="status"
              aria-live="polite"
            >
              <div className="size-9 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
              <p className="text-sm font-medium">
                {refreshingRange
                  ? `Regenerating dashboard for the ${RANGE_LABEL[refreshingRange]}…`
                  : "Regenerating dashboard…"}
              </p>
              <p className="max-w-sm text-xs text-muted-foreground">
                Switching ranges takes up to 2 minutes the first time. After that it&apos;s instant.
              </p>
            </div>
          ) : null}
        </div>
      </div>
      <RightPanel
        shareToken={shareToken}
        // Now that the share route is authenticated, derive identity from the auth store —
        // both the discussion composer and the deep-dive chat body need it.
        identity={{
          user_id: user.id,
          user_email: user.email,
          user_name: user.name || undefined,
          // Organisation may legitimately be missing on share viewers (they aren't necessarily
          // the dashboard's owner). The middleware injects the canonical org from the JWT, so
          // sending "" is safe — the field is overwritten server-side.
          organization_id: organization?.id || "",
        }}
        open={panelOpen}
        onOpenChange={handlePanelOpenChange}
        width={panelWidth}
        onWidthChange={setPanelWidth}
        onWidthCommit={handlePanelWidthCommit}
        minWidth={MIN_PANEL_WIDTH}
        maxWidth={MAX_PANEL_WIDTH}
      />
    </DashboardSectionsProvider>
  );
}

